
import React, { useEffect, useRef, useState } from 'react';
import { useLanguage } from '@/contexts/LanguageContext';
import { Button } from '@/components/ui/button';
import { Link } from 'react-router-dom';
import { ArrowRight } from 'lucide-react';

export function Hero() {
  const { t } = useLanguage();
  const heroRef = useRef<HTMLDivElement>(null);
  const [currentImage, setCurrentImage] = useState(0);
  
  // Images for the carousel
  const images = [
    "https://storage.googleapis.com/sportlogin/frontend/images/3re04z6e8f06sfd.avif",
    "https://storage.googleapis.com/sportlogin/frontend/images/fdghh0e57fsd557057.jpg",
    "https://storage.googleapis.com/sportlogin/frontend/images/fgf87yuu0okdf.jpg",
    "https://storage.googleapis.com/sportlogin/frontend/images/iyfd880014esgf505hg0gfj.jpg",
    "https://storage.googleapis.com/sportlogin/frontend/images/jhh578d0g5f05dg.jpg",
    "https://storage.googleapis.com/sportlogin/frontend/images/mlkj5hg45r05gr5t88g0d.jpg",
    "https://storage.googleapis.com/sportlogin/frontend/images/sddfgfdgh0j08uy80d0880dg7708df.jpg"

  ];
  
  useEffect(() => {
    // Image rotation timer
    const interval = setInterval(() => {
      setCurrentImage((prev) => (prev + 1) % images.length);
    }, 5000);
    
    return () => clearInterval(interval);
  }, [images.length]);
  
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach(entry => {
          if (entry.isIntersecting) {
            entry.target.classList.add('appear');
            observer.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.1 }
    );
    
    // Observe all animated elements
    if (heroRef.current) {
      const animatedElements = heroRef.current.querySelectorAll('.animated-element');
      animatedElements.forEach(el => observer.observe(el));
    }
    
    return () => observer.disconnect();
  }, []);

  return (
    <div className="relative min-h-screen flex items-center pt-24 md:pt-16" ref={heroRef}>

      {/* Background gradient */}
      <div className="absolute inset-0 bg-gradient-to-b from-blue-50 to-white -z-10" />
      
      {/* Decorative blurred circles */}
      <div className="absolute top-1/4 left-1/4 w-64 h-64 bg-blue-200 rounded-full filter blur-3xl opacity-20 animate-float -z-10" />
      <div className="absolute bottom-1/4 right-1/3 w-80 h-80 bg-purple-200 rounded-full filter blur-3xl opacity-10 animate-float -z-10" style={{ animationDelay: '2s' }} />
      
      <div className="container px-4 md:px-6 flex flex-col md:flex-row items-center">
        <div className="flex-1 text-center md:text-left pb-10 md:pb-0 md:pr-10">
          <div className="animated-element space-y-2 mb-6">
            <span className="inline-block bg-primary/10 text-primary px-3 py-1 rounded-full text-sm font-medium">
              {t('subscription.premiumAccess')}
            </span>
          </div>
          
          <h1 className="animated-element text-4xl md:text-5xl lg:text-6xl font-bold tracking-tight mb-4 text-balance">
            {t('hero.title')}
          </h1>
          
          <p className="animated-element text-lg md:text-xl text-muted-foreground mb-8 max-w-md md:max-w-xl mx-auto md:mx-0">
            {t('hero.subtitle')}
          </p>
          
          <div className="animated-element flex flex-col sm:flex-row items-center justify-center md:justify-start gap-4">
            <Link to="/register">
              <Button size="lg" className="rounded-full px-8 py-6 font-medium shadow-lg transition-all duration-300 hover:shadow-xl group">
                {t('hero.cta')}
                <ArrowRight className="ml-2 h-5 w-5 transition-transform group-hover:translate-x-1" />
              </Button>
            </Link>
            {/*<Button size="lg" variant="outline" className="rounded-full px-8 py-6 font-medium">
              {t('common.learnMore')}
            </Button>*/}
          </div>
        </div>
        
        <div className="flex-1 animated-element w-full">
          <div className="relative">
            {/* Image carousel with fade effect */}
            <div className="relative rounded-xl overflow-hidden shadow-2xl border border-black/5 h-[300px] md:h-[400px]">
              {images.map((src, index) => (
                <div 
                  key={index}
                  className="absolute inset-0 w-full h-full transition-opacity duration-1000"
                  style={{ 
                    opacity: currentImage === index ? 1 : 0,
                    zIndex: currentImage === index ? 10 : 0 
                  }}
                >
                  <img 
                    src={src} 
                    alt={`Coach planning app showcase ${index + 1}`} 
                    className="w-full h-full object-cover"
                    loading={index === 0 ? "eager" : "lazy"}
                  />
                  {/* Gradient overlay */}
                  <div className="absolute inset-0 bg-gradient-to-tr from-black/10 to-transparent pointer-events-none" />
                </div>
              ))}
            </div>
            
            {/* Floating element 1 - fixed z-index to be above images */}
            <div className="absolute -bottom-6 -left-6 glass-card rounded-lg p-4 shadow-lg animate-float z-30" style={{ animationDelay: '1s' }}>
              <div className="flex items-center space-x-3">
                <div className="h-10 w-10 bg-green-500 rounded-full flex items-center justify-center text-white">
                  <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" />
                    <polyline points="22 4 12 14.01 9 11.01" />
                  </svg>
                </div>
                <div>
                  <p className="font-medium text-sm">{t('hero.sessionComplete')}</p>
                  <p className="text-xs text-muted-foreground">{t('hero.progressUpdated')}</p>
                </div>
              </div>
            </div>
            
            {/* Floating element 2 - fixed z-index to be above images */}
            <div className="absolute -top-6 -right-6 glass-card rounded-lg p-4 shadow-lg animate-float z-30" style={{ animationDelay: '2s' }}>
              <div className="flex items-center space-x-3">
                <div className="h-10 w-10 bg-blue-500 rounded-full flex items-center justify-center text-white">
                  <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <rect x="3" y="4" width="18" height="18" rx="2" ry="2" />
                    <line x1="16" y1="2" x2="16" y2="6" />
                    <line x1="8" y1="2" x2="8" y2="6" />
                    <line x1="3" y1="10" x2="21" y2="10" />
                  </svg>
                </div>
                <div>
                  <p className="font-medium text-sm">{t('hero.newBooking')}</p>
                  <p className="text-xs text-muted-foreground">{t('hero.bookingTime')}</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
